import { Sparkles } from "lucide-react";
import type { ChatContext } from "@/store/chat-types";
import { useStudioStore } from "@/store/studio-store";
import { cn } from "@/lib/utils";
import {
  buildStudioChatOperationContext,
  getStudioChatOperationContract,
} from '@/features/chat/services/studio-chat-operation-registry'
import {
  getStudioOperationNoticeCopy,
  useStudioOperationDisplayCopy,
} from '@/features/chat/services/studio-operation-display-copy'
import type { RailItemId } from '@/shell/ide-v2/types'

interface AskCogoSurfaceButtonProps {
  context: string | (() => string);
  chatContextPatch?: Partial<ChatContext>;
  className?: string;
  label?: string;
  operationId?: string;
  operationLabel?: string;
  operationKind?: string;
  onBeforeOpen?: () => void;
  /** Keep an authority-refresh action reachable even while compact Chat is open. */
  keepVisibleWhenChatOpen?: boolean;
  /** Retain the action label in compact mode when the action must remain discoverable. */
  showLabelWhenCompact?: boolean;
}

const OPERATION_CONTEXT_RESET: Partial<ChatContext> = {
  operationId: undefined,
  operationLabel: undefined,
  operationKind: undefined,
  operationRequestedAt: undefined,
  operationContract: undefined,
  operationContextFrame: undefined,
  webMcpToolName: undefined,
  studioMcpToolName: undefined,
  webMcpToolStatus: undefined,
  chatOperationInstructions: undefined,
};

const PAGE_TARGET_RESET: Partial<ChatContext> = {
  pageId: undefined,
  pageName: undefined,
};

const SELECTION_TARGET_RESET: Partial<ChatContext> = {
  selectionSurface: undefined,
  selectedElementId: undefined,
  selectedElementLabel: undefined,
  selectedBlockName: undefined,
  selectedBlockLabel: undefined,
  selectedBlockType: undefined,
  selectedElementType: undefined,
  selectedPropertyKey: undefined,
};

const FLOW_TARGET_RESET: Partial<ChatContext> = {
  flowId: undefined,
  flowName: undefined,
};

const DATA_TARGET_RESET: Partial<ChatContext> = {
  dataSourceName: undefined,
};

const SOURCE_TARGET_RESET: Partial<ChatContext> = {
  sourceWorkspaceId: undefined,
  sourceRevision: undefined,
  sourceNodeId: undefined,
};

const DOCUMENT_TARGET_RESET: Partial<ChatContext> = {
  documentId: undefined,
  documentPageId: undefined,
  documentNodeId: undefined,
  documentProposalId: undefined,
  documentPageIndex: undefined,
  documentTarget: undefined,
};

const REPORT_TARGET_RESET: Partial<ChatContext> = {
  reportId: undefined,
  reportSectionId: undefined,
};

function clearForeignSurfaceTargets(
  context: ChatContext,
  surface: RailItemId | undefined,
): ChatContext {
  const reset = {
    ...OPERATION_CONTEXT_RESET,
    ...(surface === "preview" || surface === "editor" || surface === "behavior"
      ? {}
      : { ...PAGE_TARGET_RESET, ...SELECTION_TARGET_RESET }),
    ...(surface === "behavior" ? {} : FLOW_TARGET_RESET),
    ...(surface === "data" ? {} : DATA_TARGET_RESET),
    ...(surface === "source" ? {} : SOURCE_TARGET_RESET),
    ...(surface === "document" || surface === "report"
      ? {}
      : DOCUMENT_TARGET_RESET),
    ...(surface === "report" ? {} : REPORT_TARGET_RESET),
  };
  return { ...context, ...reset };
}

export function AskCogoSurfaceButton({
  context,
  chatContextPatch,
  className,
  label,
  operationId,
  operationLabel,
  operationKind,
  onBeforeOpen,
  keepVisibleWhenChatOpen = false,
  showLabelWhenCompact = true,
}: AskCogoSurfaceButtonProps) {
  const chatContext = useStudioStore((s) => s.chatContext)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen)
  const setChatContext = useStudioStore((s) => s.setChatContext)
  const setChatSidebarOpen = useStudioStore((s) => s.setChatSidebarOpen)
  const setPendingChatNotice = useStudioStore((s) => s.setPendingChatNotice)
  // Before the early return below — this is a hook.
  const display = useStudioOperationDisplayCopy()

  // Chat is the single general-purpose entry point. Surface controls may only
  // remain when they describe a concrete operation (for example, "Review
  // source"), never as another generic "Ask Coogo" button.
  if (!label) return null

  // The field split, at this sink. `resolvedLabel` is the ENGLISH value written
  // to `chatContext.operationLabel`, which `gateway-chat-service.ts` puts on
  // the wire next to `operationContract` — localizing it would move the model's
  // input. `visibleLabel` is what the operator reads, resolved from the
  // operation id; see `resolveStudioOperationButtonLabel` for why a caller that
  // supplies its own wording keeps it.
  const resolvedLabel = label
  const visibleLabel = display.buttonLabel(operationId, resolvedLabel)

  const askCogo = () => {
    onBeforeOpen?.();
    const nextContext = typeof context === "function" ? context() : context;
    const operation = getStudioChatOperationContract(operationId);
    const scopedContext = clearForeignSurfaceTargets(
      chatContext,
      operation?.surface.railItemId,
    );
    const mergedContext = {
      ...scopedContext,
      ...(currentProjectId ? { projectId: currentProjectId } : {}),
      ...chatContextPatch,
    };
    const operationContext = buildStudioChatOperationContext(
      operationId,
      mergedContext,
    );
    const replacesSameOperationContext = Boolean(
      operationId && chatContext.operationId === operationId,
    );
    setChatContext({
      ...mergedContext,
      ...(operationId ? { operationId } : {}),
      ...((operationLabel ?? resolvedLabel) ? { operationLabel: operationLabel ?? resolvedLabel } : {}),
      ...(operationKind ? { operationKind } : {}),
      ...(operationId ? { operationRequestedAt: Date.now() } : {}),
      ...operationContext,
      mode: "chat",
      ontologyContext: replacesSameOperationContext
        ? nextContext
        : [scopedContext.ontologyContext, nextContext]
            .filter(Boolean)
            .join("\n"),
    });
    if (operationContext.operationContract) {
      const operation = operationContext.operationContract;
      const notice = getStudioOperationNoticeCopy(display.locale);
      // The description table is a dynamic import — see the module header in
      // `studio-operation-display-copy.ts` for why the descriptions can be
      // async and the labels cannot. The notice is composed AFTER it resolves
      // and pushed once, complete, so no operator ever reads a half-localized
      // sentence. Chat still opens synchronously below; only this string waits.
      void display
        .loadDescription(operation.id, operation.description)
        .then((description) => {
          setPendingChatNotice(
            [
              notice.connected(display.label(operation.id, operation.label)),
              description,
              operation.requiresConfirmation
                ? notice.willConfirm
                : operation.readOnly
                  ? notice.readOnly
                  : null,
            ]
              .filter(Boolean)
              .join(" "),
          );
        });
    }
    setChatSidebarOpen(true);
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("cogo:focus-chat-input"));
    }, 0);
  };

  return (
    <button
      type="button"
      onClick={askCogo}
      data-testid="ask-cogo-surface-button"
      data-chat-operation-id={operationId}
      data-chat-operation-kind={operationKind}
      data-webmcp-tool-name={operationContextForDataAttr(operationId)}
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted",
        !showLabelWhenCompact &&
          "max-[1399px]:w-8 max-[1399px]:justify-center max-[1399px]:px-0",
        chatSidebarOpen &&
          !keepVisibleWhenChatOpen &&
          "max-[1399px]:hidden",
        className,
      )}
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden />
      <span className={showLabelWhenCompact ? undefined : 'max-[1399px]:sr-only'}>{visibleLabel}</span>
    </button>
  );
}

function operationContextForDataAttr(operationId: string | undefined) {
  return buildStudioChatOperationContext(operationId).webMcpToolName;
}
